// src/components/onboarding/steps/Step5ConnectActivate.tsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  OnboardingStepProps,
  DayHours,
  ProvisioningStatus,
} from "@/types/onboarding";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2,
  Check,
  AlertTriangle,
  PhoneOutgoing,
  ServerCog,
  Search,
  CheckCircle,
  XCircle,
  RotateCcw,
  Info,
} from "lucide-react";
import timezones from "timezones-list";
import { supabase } from "@/lib/supabaseClient";
import { SiGooglecalendar, SiCalendly, SiSquare } from "react-icons/si";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";

// --- SVG Component for GoHighLevel ---
const GoHighLevel = ({
  size = 24,
  color = "currentColor",
  strokeWidth = 2,
  className = "",
  ...props
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`lucide lucide-go-high-level ${className}`}
      {...props}
    >
      <path d="M5 22V7" />
      <path d="M5 7l-3 3" />
      <path d="M5 7l3 3" />
      <path d="M12 22V13" />
      <path d="M12 13l-3 3" />
      <path d="M12 13l3 3" />
      <path d="M19 22V7" />
      <path d="M19 7l-3 3" />
      <path d="M19 7l3 3" />
    </svg>
  );
};

// --- SVG Component for Acuity Scheduling ---
const AcuityScheduling = ({
  size = 24,
  color = "currentColor",
  strokeWidth = 2,
  className = "",
  ...props
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`lucide lucide-acuity-scheduling ${className}`}
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="8" r="1.5" fill={color} />
      <circle cx="12" cy="16" r="1.5" fill={color} />
    </svg>
  );
};

// Define CalendarProvider and ProviderConfig (similar to IntegrationsPage)
type CalendarProvider =
  | "google"
  | "acuity"
  | "calendly"
  | "square"
  | "ghl"
  | "outlook";

interface ProviderConfig {
  name: string;
  icon?: React.ReactNode;
  checkKey: string; // Key used by calendar-check
}

const PROVIDER_CONFIGS: Record<CalendarProvider, ProviderConfig> = {
  google: {
    name: "Google Calendar",
    checkKey: "google",
    icon: <SiGooglecalendar className="h-5 w-5 mr-2" />,
  },
  acuity: {
    name: "Acuity Scheduling",
    checkKey: "acuity",
    icon: <AcuityScheduling className="h-5 w-5 mr-2" />,
  },
  calendly: {
    name: "Calendly",
    checkKey: "calendly",
    icon: <SiCalendly className="h-5 w-5 mr-2" />,
  },
  square: {
    name: "Square Appointments",
    checkKey: "square",
    icon: <SiSquare className="h-5 w-5 mr-2" />,
  },
  ghl: {
    name: "GoHighLevel",
    checkKey: "ghl",
    icon: <GoHighLevel className="h-5 w-5 mr-2" />,
  },
  outlook: {
    name: "Outlook Calendar",
    checkKey: "outlook",
    icon: <PiMicrosoftOutlookLogoFill className="h-5 w-5 mr-2" />,
  },
};

// Keep Type Guard
function isDayHours(obj: any): obj is DayHours {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof obj.enabled === "boolean" &&
    typeof obj.start === "string" &&
    typeof obj.end === "string"
  );
}

// Updated Interfaces
interface CalendarCheckResponse {
  status: string;
  error?: string;
  missingVars?: string[];
  configuredProviders?: string[]; // Ensure this matches backend response
}
interface CalendarIntegration {
  id: string;
  provider: CalendarProvider; // Updated type
  account_email: string;
  status: string;
}
interface ProvisioningResponse {
  status: ProvisioningStatus;
  phoneNumber?: string | null;
  message?: string;
  error?: string;
}
interface GetStatusResponse {
  status: ProvisioningStatus;
  error?: string | null;
  phoneNumber?: string | null;
}

const Step5ConnectActivate: React.FC<OnboardingStepProps> = ({
  data,
  updateData,
  goToNextStep,
  goToPrevStep,
  triggerSave,
  isSaving,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isCheckingBackendConfig, setIsCheckingBackendConfig] = useState(true);
  const [backendConfigured, setBackendConfigured] = useState<boolean | null>(
    null
  );
  const [backendConfigError, setBackendConfigError] = useState<string | null>(
    null
  );
  const [configuredProviders, setConfiguredProviders] = useState<string[]>([]);

  const [connectedProvider, setConnectedProvider] =
    useState<CalendarProvider | null>(data.calendarProvider || null);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(
    data.calendarEmail || null
  );
  const [isActivating, setIsActivating] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 1. Backend Environment Configuration Check
  useEffect(() => {
    setIsCheckingBackendConfig(true);
    setBackendConfigured(null);
    setBackendConfigError(null);
    setConfiguredProviders([]);

    const checkBackendConfig = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session)
          throw new Error(sessionError?.message || "Not authenticated");

        const functionUrl = `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/calendar-check`;
        const res = await fetch(functionUrl, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const json: CalendarCheckResponse = await res.json();

        if (!res.ok || json.error) {
          const errorMsg =
            json.error ||
            `Configuration check failed with status ${res.status}`;
          const detailedMsg =
            json.missingVars &&
            Array.isArray(json.missingVars) &&
            json.missingVars.length > 0
              ? `${errorMsg}: Missing ${json.missingVars.join(", ")}.`
              : errorMsg;
          throw new Error(detailedMsg);
        }

        setConfiguredProviders(json.configuredProviders || []);
        setBackendConfigured(true);
      } catch (e: any) {
        console.error("[DEBUG_FRONTEND] Backend config check failed:", e);
        setBackendConfigured(false); // Explicitly false on error for clarity
        setBackendConfigError(
          e.message || "An unknown error occurred during backend check."
        );
        if (e.message && e.message.includes("Missing")) {
          toast.warning(`Calendar backend partially configured: ${e.message}`, {
            duration: 10000,
          });
        } else {
          toast.error(`Calendar backend misconfigured: ${e.message}`, {
            duration: 10000,
          });
        }
      } finally {
        setIsCheckingBackendConfig(false);
      }
    };
    checkBackendConfig();
  }, []);

  // 2. Query USER'S actual integration status
  const {
    data: fetchedIntegrations = [],
    isLoading: integrationStatusLoading,
    error: integrationStatusError,
  } = useQuery<CalendarIntegration[], Error>({
    queryKey: ["userCalendarIntegrationsOnboarding", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("User not available");
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session)
        throw new Error(sessionError?.message || "Not authenticated");
      const functionUrl = `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/calendar-integration`;
      const res = await fetch(functionUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        let errorJson: { error?: string } = {};
        try {
          errorJson = await res.json();
        } catch {
          /* ignore */
        }
        throw new Error(
          errorJson?.error || `Failed to fetch integrations (${res.status})`
        );
      }
      const integrations: CalendarIntegration[] = await res.json();
      return integrations;
    },
    enabled:
      !!user?.id && !isCheckingBackendConfig && backendConfigured !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // 3. Effect to update local connection state based on query
  useEffect(() => {
    let activeIntegration: CalendarIntegration | undefined = undefined;
    if (fetchedIntegrations && fetchedIntegrations.length > 0) {
      activeIntegration = fetchedIntegrations.find(
        (int) =>
          (Object.keys(PROVIDER_CONFIGS) as CalendarProvider[]).includes(
            int.provider
          ) &&
          (int.status === "active" || int.status === "active_watching")
      );
    }

    if (activeIntegration) {
      if (
        connectedEmail !== activeIntegration.account_email ||
        connectedProvider !== activeIntegration.provider
      ) {
        setConnectedProvider(activeIntegration.provider);
        setConnectedEmail(activeIntegration.account_email);
        // Update onboardingData if it's out of sync with the fetched active integration
        if (
          data.calendarEmail !== activeIntegration.account_email ||
          data.calendarProvider !== activeIntegration.provider
        ) {
          updateData({
            calendarProvider: activeIntegration.provider,
            calendarEmail: activeIntegration.account_email,
          });
        }
      }
    } else {
      // No active integration found, ensure local and onboardingData states are null
      if (connectedEmail !== null || connectedProvider !== null) {
        setConnectedProvider(null);
        setConnectedEmail(null);
      }
      if (data.calendarEmail !== null || data.calendarProvider !== null) {
        updateData({ calendarProvider: null, calendarEmail: null });
      }
    }
  }, [
    fetchedIntegrations,
    updateData,
    data.calendarProvider,
    data.calendarEmail,
    connectedEmail,
    connectedProvider,
  ]);

  // 4. Mutation to initiate OAuth
  const connectCalendar = useMutation({
    mutationFn: async (provider: CalendarProvider) => {
      if (backendConfigured !== true)
        throw new Error(
          "Backend system not fully configured for calendar connections."
        );
      if (!configuredProviders.includes(PROVIDER_CONFIGS[provider].checkKey)) {
        throw new Error(
          `${PROVIDER_CONFIGS[provider].name} is not available due to server configuration.`
        );
      }
      if (connectedProvider) {
        throw new Error(
          "A calendar is already connected. Please proceed or refresh if this is an error."
        );
      }

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session)
        throw new Error(sessionError?.message || "Not authenticated");

      const returnPath = location.pathname;
      const functionUrl = `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/calendar-integration`;
      const res = await fetch(functionUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ provider, returnPath }),
      });
      const responseData = await res.json();
      if (!res.ok)
        throw new Error(
          responseData.error ||
            responseData.message ||
            `Failed to initiate connection (${res.status})`
        );
      if (!responseData.url)
        throw new Error("Received invalid auth URL from server.");
      return responseData.url as string;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: any) => {
      toast.error(`Connect failed: ${err.message}`);
    },
  });

  // 5. Effect to Handle OAuth Callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const successFlag = params.get("success");
    const errorFlag = params.get("error_description") || params.get("error");

    if (!successFlag && !errorFlag) return;
    if (!user || !user.id) {
      console.warn(
        "OAuth callback processing skipped: user not available yet."
      );
      return;
    }

    const currentUserId = user.id;
    queryClient.invalidateQueries({
      queryKey: ["userCalendarIntegrationsOnboarding", currentUserId],
    });

    const processCallback = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session)
          throw (
            sessionError ||
            new Error("Not authenticated for callback processing")
          );

        if (successFlag) {
          toast.success(`Calendar connection initiated! Checking status...`);
        } else if (errorFlag) {
          toast.error(
            `Calendar connection failed: ${decodeURIComponent(errorFlag)}`
          );
        }
      } catch (error: any) {
        if (!errorFlag) {
          toast.error(
            `Failed to process calendar connection: ${error.message}`
          );
        }
      } finally {
        navigate(location.pathname, { replace: true }); // Clear query params
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search, navigate, queryClient]); // updateData removed as it's handled by integration fetch effect

  const getProviderDisplayName = (
    providerKey: CalendarProvider | string
  ): string => {
    if (providerKey in PROVIDER_CONFIGS) {
      return PROVIDER_CONFIGS[providerKey as CalendarProvider].name;
    }
    return providerKey.charAt(0).toUpperCase() + providerKey.slice(1);
  };

  const handleFunctionChange = (
    funcName: keyof typeof data.callActivationSettings,
    checked: boolean
  ) => {
    updateData({
      callActivationSettings: {
        ...data.callActivationSettings,
        [funcName]: { enabled: checked },
      },
    });
    if (checked) {
      setErrors((prev) => ({ ...prev, functions: undefined }));
    }
  };
  const selectedFunctionsCount = Object.values(
    data.callActivationSettings
  ).filter((f) => f.enabled).length;

  const showCustomHours = !data.aiCallingHours.useBusinessHours;

  const [errors, setErrors] = useState<{
    functions?: string;
    callingHours?: string;
  }>({});
  const handleTimezoneChange = (timezone: string) => {
    updateData({ aiCallingHours: { ...data.aiCallingHours, timezone } });
  };
  const handleDayToggle = (
    day: keyof typeof data.aiCallingHours.days,
    checked: boolean
  ) => {
    updateData({
      aiCallingHours: {
        ...data.aiCallingHours,
        days: {
          ...data.aiCallingHours.days,
          [day]: {
            ...(data.aiCallingHours.days[day] as DayHours),
            enabled: checked,
          },
        },
      },
    });
  };
  const handleTimeChange = (
    day: keyof typeof data.aiCallingHours.days,
    type: "start" | "end",
    value: string
  ) => {
    updateData({
      aiCallingHours: {
        ...data.aiCallingHours,
        days: {
          ...data.aiCallingHours.days,
          [day]: {
            ...(data.aiCallingHours.days[day] as DayHours),
            [type]: value,
          },
        },
      },
    });
  };
  const timezoneOptions = useMemo(
    () => timezones.map((tz) => ({ label: tz.label, value: tz.tzCode })),
    []
  );

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  const pollProvisioningStatus = async () => {
    console.log("[DEBUG_POLLING] Polling for status...");
    try {
      const { data: result, error } =
        await supabase.functions.invoke<GetStatusResponse>(
          "get-provisioning-status"
        );
      if (error) {
        let specificPollError = error.message || "Failed to get status.";
        const pollErrorContext = (error as any).context;
        if (pollErrorContext && typeof pollErrorContext.json === "function") {
          try {
            const errorJson = await pollErrorContext.json();
            if (
              errorJson &&
              typeof errorJson.error === "string" &&
              errorJson.error
            ) {
              specificPollError = errorJson.error;
            }
          } catch (e) {
            console.error(
              "Failed to parse error JSON from polling context:",
              e
            );
          }
        } else if (
          pollErrorContext &&
          pollErrorContext.json &&
          typeof pollErrorContext.json.error === "string" &&
          pollErrorContext.json.error
        ) {
          specificPollError = pollErrorContext.json.error;
        }
        updateData({ provisioningError: specificPollError });
        stopPolling();
        setIsActivating(false);
        toast.error(specificPollError);
        return;
      }
      if (result) {
        console.log("[DEBUG_POLLING] Received status:", result.status);
        updateData({
          provisioningStatus: result.status,
          provisioningError: result.error,
          assignedPhoneNumber:
            result.status === "success"
              ? result.phoneNumber ?? data.assignedPhoneNumber
              : data.assignedPhoneNumber,
        });
        if (result.status === "success" || result.status === "failed") {
          stopPolling();
          setIsActivating(false);
          if (result.status === "failed") {
            toast.error(result.error || "Activation failed: Unknown reason");
          }
        }
      }
    } catch (err: any) {
      updateData({ provisioningError: err.message || "Polling failed." });
      stopPolling();
      setIsActivating(false);
      toast.error(err.message || "Error checking activation status.");
    }
  };
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const activateAssistantMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        aiName: data.aiName,
        businessName: data.businessName,
        greeting: data.aiGreetingMessage,
        forwardingNumber: data.forwardingNumber || null,
        voiceId: data.aiVoiceId,
        voiceProvider: data.aiVoiceProvider || "vapi",
        hipaa_enabled: data.hipaa_enabled,
        default_appointment_duration_minutes:
          data.defaultAppointmentDurationMinutes,
        ai_calling_hours: data.aiCallingHours,
      };
      const { data: result, error: invokeError } =
        await supabase.functions.invoke<ProvisioningResponse>(
          "provision-and-configure-assistant",
          { body: payload }
        );

      if (invokeError) {
        let specificMessage =
          "Activation failed. Please check details and try again.";
        const context = (invokeError as any).context;

        if (context && typeof context.json === "function") {
          try {
            const errorJson = await context.json();
            if (
              errorJson &&
              typeof errorJson.error === "string" &&
              errorJson.error
            ) {
              specificMessage = errorJson.error;
            } else if (
              errorJson &&
              typeof errorJson.message === "string" &&
              errorJson.message
            ) {
              specificMessage = errorJson.message;
            }
          } catch (e) {
            console.error("Failed to parse JSON from error context:", e);
            if (
              invokeError.message &&
              !invokeError.message
                .toLowerCase()
                .includes("function returned an error") &&
              !invokeError.message.toLowerCase().includes("fetch failed")
            ) {
              specificMessage = invokeError.message;
            }
          }
        } else if (invokeError.message) {
          if (
            !invokeError.message
              .toLowerCase()
              .includes("function returned an error") &&
            !invokeError.message.toLowerCase().includes("fetch failed")
          ) {
            specificMessage = invokeError.message;
          }
        }
        throw new Error(specificMessage);
      }

      if (!result) {
        throw new Error("No response from activation function.");
      }
      updateData({
        provisioningStatus: result.status,
        provisioningError: result.error || null,
        assignedPhoneNumber: result.phoneNumber ?? data.assignedPhoneNumber,
      });
      return result;
    },
    onSuccess: (result) => {
      if (result.status === "success") {
        toast.success("AI Employee Activated!");
        setIsActivating(false);
        stopPolling();
        setTimeout(() => goToNextStep(), 500);
      } else if (result.status === "failed") {
        setIsActivating(false);
        stopPolling();
        toast.error(result.error || "Activation failed: Unknown reason");
      } else {
        pollProvisioningStatus();
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(
            pollProvisioningStatus,
            3000
          );
        }
      }
    },
    onError: (error: Error) => {
      setIsActivating(false);
      stopPolling();
      updateData({
        provisioningStatus: "failed",
        provisioningError: error.message,
      });
      toast.error(error.message);
    },
  });

  const canActivate = useMemo(() => {
    const atLeastOneFunction = selectedFunctionsCount > 0;
    const callingHoursValid =
      data.aiCallingHours.useBusinessHours ||
      Object.values(data.aiCallingHours.days)
        .filter((d) => isDayHours(d))
        .some((d) => d.enabled);

    // Ensures initial calendar system checks are complete before allowing activation.
    const calendarSystemReady =
      !isCheckingBackendConfig &&
      backendConfigured !== null &&
      !integrationStatusLoading;

    return (
      calendarSystemReady &&
      atLeastOneFunction &&
      callingHoursValid &&
      !isSaving &&
      !isActivating
    );
  }, [
    isCheckingBackendConfig,
    backendConfigured,
    integrationStatusLoading,
    selectedFunctionsCount,
    data.aiCallingHours,
    isSaving,
    isActivating,
  ]);

  const handleActivate = async () => {
    const newErrors: typeof errors = {};
    if (selectedFunctionsCount === 0)
      newErrors.functions = "Please select at least one function.";

    if (!data.aiCallingHours.useBusinessHours) {
      const customHoursValid = Object.values(data.aiCallingHours.days)
        .filter((d) => isDayHours(d))
        .some((d) => d.enabled);
      if (!customHoursValid)
        newErrors.callingHours =
          "Please enable at least one day for custom calling hours.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (!triggerSave) {
      toast.error("Internal error: Cannot save settings.");
      return;
    }
    const saveSuccess = await triggerSave();
    if (!saveSuccess) {
      toast.error("Failed to save settings. Cannot activate.");
      return;
    }
    setIsActivating(true);
    updateData({
      provisioningStatus: "provisioning_started",
      provisioningError: null,
    });
    activateAssistantMutation.mutate();
  };

  const getStatusIndicator = () => {
    const status = data.provisioningStatus;
    const error = data.provisioningError;
    if (
      !isActivating &&
      status !== "success" &&
      status !== "failed" &&
      status !== "provisioning_started" &&
      status !== "searching_twilio_number" &&
      status !== "purchasing_twilio_number" &&
      status !== "configuring_vapi_assistant" &&
      status !== "releasing_twilio_number_on_error" &&
      status !== "registering_number_with_vapi"
    ) {
      return null;
    }
    switch (status) {
      case "provisioning_started":
        return (
          <>
            <Loader2 size={16} className="animate-spin mr-2" /> Starting
            activation...
          </>
        );
      case "searching_twilio_number":
        return (
          <>
            <Search size={16} className="animate-spin mr-2" /> Searching for
            number...
          </>
        );
      case "purchasing_twilio_number":
        return (
          <>
            <PhoneOutgoing size={16} className="animate-spin mr-2" /> Purchasing
            number...
          </>
        );
      case "configuring_vapi_assistant":
        return (
          <>
            <ServerCog size={16} className="animate-spin mr-2" /> Connecting
            number to AI...
          </>
        );
      case "releasing_twilio_number_on_error":
        return (
          <>
            <RotateCcw size={16} className="animate-spin mr-2" /> Cleaning up...
          </>
        );
      case "registering_number_with_vapi":
        return (
          <>
            <ServerCog size={16} className="animate-spin mr-2" /> Finalizing
            setup...
          </>
        );
      case "success":
        return (
          <>
            <CheckCircle size={16} className="text-green-500 mr-2" /> Activation
            Complete!
          </>
        );
      case "failed":
        return (
          <>
            <XCircle size={16} className="text-red-500 mr-2" />{" "}
            {error || "Activation Failed: Unknown error"}
          </>
        );
      default:
        return isActivating ? (
          <>
            <Loader2 size={16} className="animate-spin mr-2" /> Activating
            (Status: {status})...
          </>
        ) : null;
    }
  };

  const renderCalendarConnectionSection = () => {
    if (
      isCheckingBackendConfig ||
      (backendConfigured === null && !backendConfigError)
    ) {
      return (
        <div className="flex items-center p-3 bg-blue-100 dark:bg-blue-900/50 border border-blue-300 dark:border-blue-700 rounded-md mb-4">
          <Loader2
            size={16}
            className="animate-spin mr-2 text-blue-600 dark:text-blue-400"
          />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Checking system configuration...
          </span>
        </div>
      );
    }

    if (
      backendConfigured === false &&
      backendConfigError &&
      configuredProviders.length === 0
    ) {
      return (
        <div className="p-3 mb-6 rounded-md border border-red-300 bg-red-50 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 text-sm flex items-center">
          <AlertTriangle size={16} className="mr-2" /> Critical System Error:{" "}
          {backendConfigError}. Calendar connections are unavailable.
        </div>
      );
    }

    if (
      backendConfigured === false &&
      backendConfigError &&
      configuredProviders.length > 0
    ) {
      return (
        <div
          className="p-3 mb-4 text-sm text-yellow-800 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300"
          role="alert"
        >
          <div className="flex items-center">
            <Info className="inline w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-400" />
            <span className="font-semibold">Configuration Notice:</span>
          </div>
          <p className="ml-7">
            {backendConfigError} Some providers may be unavailable.
          </p>
        </div>
      );
    }

    if (
      integrationStatusLoading &&
      !fetchedIntegrations.length &&
      backendConfigured === true
    ) {
      return (
        <div className="flex items-center p-3 bg-blue-100 dark:bg-blue-900/50 border border-blue-300 dark:border-blue-700 rounded-md mb-4">
          <Loader2
            size={16}
            className="animate-spin mr-2 text-blue-600 dark:text-blue-400"
          />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Checking calendar connection...
          </span>
        </div>
      );
    }

    if (integrationStatusError && !integrationStatusLoading) {
      return (
        <div className="flex items-center p-3 bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-700 rounded-md mb-4">
          <AlertTriangle
            size={16}
            className="mr-2 text-red-600 dark:text-red-400"
          />
          <span className="text-sm font-medium text-red-800 dark:text-red-200">
            Error checking calendar: {integrationStatusError.message}
          </span>
        </div>
      );
    }

    if (connectedProvider && connectedEmail) {
      return (
        <div className="flex items-center justify-between p-3 bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-700 rounded-md">
          <div className="flex items-center">
            <Check
              size={20}
              className="text-green-600 dark:text-green-400 mr-2"
            />
            <span className="text-sm font-medium text-green-800 dark:text-green-200">
              Connected: {getProviderDisplayName(connectedProvider)} -{" "}
              {connectedEmail}
            </span>
          </div>
        </div>
      );
    }

    // If not connected, show connection options
    if (
      backendConfigured === true ||
      (backendConfigured === false && configuredProviders.length > 0)
    ) {
      const availableProviders = (
        Object.keys(PROVIDER_CONFIGS) as CalendarProvider[]
      ).filter((key) => PROVIDER_CONFIGS[key]);

      if (
        availableProviders.length === 0 &&
        configuredProviders.length === 0 &&
        backendConfigured === true
      ) {
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No calendar providers are currently configured for connection.
          </p>
        );
      }

      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(Object.keys(PROVIDER_CONFIGS) as CalendarProvider[]).map(
              (providerKey) => {
                const providerInfo = PROVIDER_CONFIGS[providerKey];
                const isProviderConfiguredByBackend =
                  configuredProviders.includes(providerInfo.checkKey);

                const finalIsDisabled =
                  connectCalendar.isPending ||
                  integrationStatusLoading ||
                  !!connectedProvider ||
                  !isProviderConfiguredByBackend;

                return (
                  <Button
                    key={providerKey}
                    onClick={() => connectCalendar.mutate(providerKey)}
                    disabled={finalIsDisabled}
                    variant={
                      isProviderConfiguredByBackend ? "default" : "outline"
                    }
                    className={`w-full justify-start text-left ${
                      finalIsDisabled ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {providerInfo.icon}
                    <div className="flex flex-col ml-1">
                      <span>
                        {connectCalendar.isPending &&
                        connectCalendar.variables === providerKey
                          ? "Connecting..."
                          : `Connect ${providerInfo.name}`}
                      </span>
                      {!isProviderConfiguredByBackend && (
                        <span className="text-xs font-normal opacity-80">
                          (Admin setup needed)
                        </span>
                      )}
                      {providerKey === "calendly" && (
                        <span className="text-xs font-normal opacity-80">
                          (Paid account required)
                        </span>
                      )}
                    </div>
                  </Button>
                );
              }
            )}
          </div>
          {connectCalendar.isError && (
            <p className="text-xs text-red-600 mt-2">
              Connection Error: {connectCalendar.error?.message}
            </p>
          )}
          {backendConfigured === true && configuredProviders.length === 0 && (
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
              No calendar providers seem to be enabled in the backend
              configuration.
            </p>
          )}
        </>
      );
    }

    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 italic">
        Calendar connection status is undetermined or connection is not possible
        due to system configuration.
      </p>
    );
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-gray-100">
        Final Step: Connect, Configure & Activate!
      </h2>

      <div className="space-y-8">
        <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-lg font-semibold mb-1 text-gray-800 dark:text-gray-100">
            1. Connect Your Calendar
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Connect a calendar to enable automated appointment management. This
            is highly recommended, but you can skip this step and connect one
            later from your dashboard.
            <br />
            <br />
            <strong>Note:</strong> Calendly integration requires a paid Calendly
            account (Professional, Team, or Enterprise plan). Free accounts are
            not supported.
          </p>
          {renderCalendarConnectionSection()}
        </div>

        {/* Sections for AI Functions, Calling Hours, and Activation Button */}
        {/* These are shown once initial calendar system checks are complete, regardless of connection status */}
        {!isCheckingBackendConfig &&
          backendConfigured !== null &&
          !integrationStatusLoading && (
            <>
              <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                  2. Select Initial AI Functions
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Choose the primary tasks for your AI. (Select at least one)
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="confirm_appointments"
                      checked={
                        data.callActivationSettings.confirm_appointments.enabled
                      }
                      onCheckedChange={(checked) =>
                        handleFunctionChange("confirm_appointments", !!checked)
                      }
                    />
                    <Label
                      htmlFor="confirm_appointments"
                      className="font-normal text-gray-700 dark:text-gray-300"
                    >
                      Confirm upcoming appointments
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="recover_cancellations"
                      checked={
                        data.callActivationSettings.recover_cancellations
                          .enabled
                      }
                      onCheckedChange={(checked) =>
                        handleFunctionChange("recover_cancellations", !!checked)
                      }
                    />
                    <Label
                      htmlFor="recover_cancellations"
                      className="font-normal text-gray-700 dark:text-gray-300"
                    >
                      Recover recently cancelled appointments
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reschedule_noshows"
                      checked={
                        data.callActivationSettings.reschedule_noshows.enabled
                      }
                      onCheckedChange={(checked) =>
                        handleFunctionChange("reschedule_noshows", !!checked)
                      }
                    />
                    <Label
                      htmlFor="reschedule_noshows"
                      className="font-normal text-gray-700 dark:text-gray-300"
                    >
                      Reschedule no-shows
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="inbound_calls"
                      checked={true}
                      disabled={true}
                    />
                    <Label
                      htmlFor="inbound_calls"
                      className="font-normal text-gray-500 dark:text-gray-500"
                    >
                      Receive inbound calls (Always Active)
                    </Label>
                  </div>
                </div>
                {errors.functions && (
                  <p className="text-xs text-red-600 mt-2">
                    {errors.functions}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  {connectedProvider && connectedEmail
                    ? "We recommend starting with confirmations."
                    : "Connect a calendar to unlock full functionality for these tasks."}
                </p>
              </div>

              <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                  3. Define AI Calling Hours
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Set when your AI can make outbound calls.
                </p>
                <div className="mb-4">
                  <Label
                    htmlFor="timezone"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Timezone
                  </Label>
                  <Select
                    value={data.aiCallingHours.timezone}
                    onValueChange={handleTimezoneChange}
                  >
                    <SelectTrigger id="timezone" className="mt-1">
                      <SelectValue placeholder="Select timezone..." />
                    </SelectTrigger>
                    <SelectContent>
                      {timezoneOptions.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {showCustomHours && (
                  <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Custom Calling Window:
                    </h4>
                    {Object.entries(data.aiCallingHours.days).map(
                      ([day, hours]) =>
                        isDayHours(hours) && (
                          <div
                            key={day}
                            className="grid grid-cols-4 items-center gap-x-3 gap-y-1 text-sm"
                          >
                            <div className="flex items-center col-span-1">
                              <Checkbox
                                id={`day-${day}`}
                                checked={hours.enabled}
                                onCheckedChange={(checked) =>
                                  handleDayToggle(
                                    day as keyof typeof data.aiCallingHours.days,
                                    !!checked
                                  )
                                }
                                className="mr-2"
                              />
                              <Label
                                htmlFor={`day-${day}`}
                                className="font-normal text-gray-700 dark:text-gray-300 capitalize"
                              >
                                {day}
                              </Label>
                            </div>
                            <div className="col-span-1">
                              <Label
                                htmlFor={`start-${day}`}
                                className="sr-only"
                              >
                                Start time for {day}
                              </Label>
                              <Input
                                id={`start-${day}`}
                                type="time"
                                value={hours.start}
                                onChange={(e) =>
                                  handleTimeChange(
                                    day as keyof typeof data.aiCallingHours.days,
                                    "start",
                                    e.target.value
                                  )
                                }
                                disabled={!hours.enabled}
                                className="py-1 px-2 h-8"
                              />
                            </div>
                            <span className="text-center text-gray-500 col-span-1">
                              -
                            </span>
                            <div className="col-span-1">
                              <Label htmlFor={`end-${day}`} className="sr-only">
                                End time for {day}
                              </Label>
                              <Input
                                id={`end-${day}`}
                                type="time"
                                value={hours.end}
                                onChange={(e) =>
                                  handleTimeChange(
                                    day as keyof typeof data.aiCallingHours.days,
                                    "end",
                                    e.target.value
                                  )
                                }
                                disabled={!hours.enabled}
                                className="py-1 px-2 h-8"
                              />
                            </div>
                          </div>
                        )
                    )}
                    {errors.callingHours && (
                      <p className="text-xs text-red-600 mt-2">
                        {errors.callingHours}
                      </p>
                    )}
                  </div>
                )}
                {!showCustomHours && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    AI will use the business hours defined in Step 2 for
                    outbound calls.
                  </p>
                )}
              </div>

              <div className="mt-8 text-center">
                <Button
                  size="lg"
                  onClick={handleActivate}
                  disabled={!canActivate || isSaving || isActivating}
                  className="w-full md:w-auto"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={20} className="animate-spin mr-2" /> Saving
                      Settings...
                    </>
                  ) : isActivating ? (
                    <>
                      <Loader2 size={20} className="animate-spin mr-2" />{" "}
                      Activating AI...
                    </>
                  ) : (
                    "Activate My AI Employee!"
                  )}
                </Button>
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400 min-h-[40px] flex items-center justify-center px-2 py-1">
                  {getStatusIndicator()}
                </div>
                {!canActivate && !isSaving && !isActivating && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 px-4">
                    Please ensure at least one AI function is selected and
                    calling hours are confirmed to activate.
                    {!isCheckingBackendConfig &&
                      backendConfigured !== null &&
                      !integrationStatusLoading &&
                      !connectedProvider &&
                      !connectedEmail &&
                      " Connecting a calendar is recommended for full functionality and can be done later from your dashboard."}
                  </p>
                )}
              </div>
            </>
          )}
      </div>

      <div className="mt-10 flex justify-between">
        <Button
          variant="outline"
          onClick={goToPrevStep}
          disabled={
            !goToPrevStep ||
            isSaving ||
            isActivating ||
            connectCalendar.isPending ||
            activateAssistantMutation.isPending
          }
        >
          Back
        </Button>
        <div></div>
      </div>
    </div>
  );
};

export default Step5ConnectActivate;